<template>
  <v-row no-gutters align="center">
    <v-col cols="12">
      <v-row no-gutters align="center">
        <v-col cols="auto" class="pr-2 text-left font-weight-medium">
          {{ $i18n.t('admin.treeviews.configuration') }}
        </v-col>
        <v-col cols="auto" class="pr-2 text-left">
          <base-rounded-button
            icon="mdi-plus"
            size="x-small"
            color="success"
            @click="dialogAction('add')"
            :tooltip="$i18n.t('admin.treeviews.addConfiguration')"
          />
        </v-col>
      </v-row>
      <v-row no-gutters class="pt-7">
        <v-col cols="5">
          <ribbon-list
            :listItems="[...treeviews]"
            item-title-key="name"
            item-subtitle-key=""
            no-items-translation-path="default.noData"
            style-type="rawHigher"
            :toggleable="false"
          >
            <template #itemButtons-replace="{ item }">
              <dots-menu :items="dotsItems" v-on="getDataTreeDotsListeners(item)" />
            </template>
          </ribbon-list>
        </v-col>
      </v-row>
    </v-col>
    <main-dialog
      v-if="isMainDialogLoaded"
      :is-visible.sync="isMainDialogVisible"
      v-bind="mainDialogBind"
      @agree="dialogActionHandler"
    />
    <treeview-settings-dialog
      v-if="isTreeviewSettingsDialogLoaded"
      :is-visible.sync="isTreeviewSettingsDialogVisible"
      v-bind="treeviewSettingsDialogBind"
      :configuration.sync="currentConfiguration"
      @agree="dialogActionHandler"
    />
  </v-row>
</template>
<script>
import { call, get } from 'vuex-pathify';

import DotsMenu from '@/components/DotsMenu';
import RibbonList from '@/components/RibbonList';

export default {
  name: 'TreeviewsSettingsList',
  components: {
    DotsMenu,
    RibbonList,
    MainDialog: () => import('@/components/MainDialog'),
    TreeviewSettingsDialog: () => import('@/components/TreeviewSettingsDialog'),
  },
  computed: {
    treeviews: get('attributesSchema/treeviews'),

    dialogsActionsData() {
      return {
        add: {
          agreePath: 'dialog.agreeAdd',
          component: 'TreeviewSettingsDialog',
          initAction: this.setCurrentConfiguration,
        },
        delete: {
          agreePath: 'dialog.agreeDelete',
          component: 'MainDialog',
        },
        edit: {
          agreePath: 'dialog.agreeSave',
          component: 'TreeviewSettingsDialog',
          initAction: this.setCurrentConfiguration,
        },
      };
    },

    mainDialogBind() {
      return {
        loading: this.isMainDialogLoading,
        ...(this.isMainDialogVisible
          ? {
              title: this.$i18n.t(`admin.treeviews.actions.${this.currentAction}Configuration`),
              body: this.$i18n.t(
                `admin.treeviews.actions.confirmConfiguration${this.capitalizeCurrentAction}`,
                this.currentActionData
              ),
              agreeText: this.currentDialogActionData.agreePath,
            }
          : {}),
      };
    },
    treeviewSettingsDialogBind() {
      return {
        loading: this.isTreeviewSettingsDialogLoading,
        dialogWidth: '50%',
        ...(this.isTreeviewSettingsDialogVisible
          ? {
              title: this.$i18n.t(`admin.treeviews.actions.${this.currentAction}Configuration`),
              agreeText: this.currentDialogActionData.agreePath,
            }
          : {}),
      };
    },

    capitalizeCurrentAction() {
      return this.$_capitalize(this.currentAction);
    },
    currentDialogActionData() {
      return this.dialogsActionsData[this.currentAction] || {};
    },
  },
  data: () => ({
    currentAction: '',
    currentActionData: undefined,

    currentConfiguration: undefined,

    dotsItems: [{ name: 'edit' }, { name: 'delete' }],

    isMainDialogLoaded: false,
    isMainDialogLoading: false,
    isMainDialogVisible: false,
    isTreeviewSettingsDialogLoaded: false,
    isTreeviewSettingsDialogLoading: false,
    isTreeviewSettingsDialogVisible: false,
  }),
  methods: {
    addTreeview: call('attributesSchema/addTreeview'),
    deleteTreeview: call('attributesSchema/deleteTreeview'),
    editTreeview: call('attributesSchema/editTreeview'),
    getDataTreeDotsListeners(item) {
      return this.dotsItems.reduce((total, action) => {
        total[action.name] = () => {
          this.dialogAction(action.name, item);
        };
        return total;
      }, {});
    },

    dialogAction(type, value) {
      this.currentAction = type;
      this.currentActionData = JSON.parse(JSON.stringify(value || {}));
      const { component, initAction } = this.dialogsActionsData[type];
      if (initAction) initAction(value ? JSON.parse(JSON.stringify(value)) : undefined);
      this[`is${component}Loaded`] = true;
      this[`is${component}Loading`] = false;
      this[`is${component}Visible`] = true;
    },
    async dialogActionHandler(data) {
      const dialogComponent = this.currentDialogActionData?.component;
      this[`is${dialogComponent}Loading`] = true;
      try {
        await this[`handler${this.capitalizeCurrentAction}TreeviewSetting`](data);
        this[`is${this.currentDialogActionData?.component}Visible`] = false;
        this[`is${dialogComponent}Loading`] = false;
      } catch {
        this[`is${dialogComponent}Loading`] = false;
      }
    },
    async handlerAddTreeviewSetting(data) {
      console.log('Add', data);
      await this.addTreeview(data);
    },
    async handlerDeleteTreeviewSetting() {
      console.log('Delete', this.currentActionData);
      await this.deleteTreeview(this.currentActionData.id);
    },
    async handlerEditTreeviewSetting(data) {
      await this.editTreeview({ id: data.id, payload: data });
    },

    setCurrentConfiguration(value) {
      const keys = [
        'badges',
        'child_attribute',
        'children',
        'datasource',
        'id',
        'layer',
        'max_occurance',
        'min_occurance',
        'name',
        'order',
        'parent_attribute',
        'parent_id',
        'subtitle',
        'title',
      ];
      if (value) {
        value = keys.reduce((total, current) => {
          total[current] = value[current];
          return total;
        }, {});

        const sortElementsInTree = tree => {
          const fn = elements => {
            return elements.map(element => {
              if (element.children) element.children.sort((a, b) => a.order - b.order);
              return {
                ...element,
                ...(element.children ? { children: fn(element.children) } : {}),
              };
            });
          };
          return fn(tree);
        };

        const { id, name } = value;
        delete value.id;
        delete value.name;
        this.currentConfiguration = JSON.parse(JSON.stringify({ id, name, tree: sortElementsInTree([value]) })); // Formater backend -> frontend
      } else {
        this.currentConfiguration = {
          name: '',
          tree: [
            {
              layer: null,
              child_attribute: null,
              title: '',
              subtitle: '',
              badges: [],
              children: [
                {
                  layer: null,
                  child_attribute: null,
                  title: '',
                  subtitle: '',
                  badges: [],
                  children: [],
                },
              ],
            },
          ],
        };
      }
    },
  },
  mounted() {
    this.$watch(
      vm => [vm.isMainDialogVisible, vm.isTreeviewSettingsDialogVisible],
      nV => {
        const [isMainDialogVisible, isTreeviewSettingsDialogVisible] = nV;
        if (!isMainDialogVisible && !isTreeviewSettingsDialogVisible) {
          this.currentAction = '';
          this.currentActionData = undefined;
          this.isMainDialogLoading = false;
          this.isTreeviewSettingsDialogLoading = false;
        }
      }
    );
  },
};
</script>
